import { RRule } from "rrule";

interface Exception {
  id: string;
  rrule?: string; // RRULE pattern for recurring
  date?: string; // YYYY-MM-DD for one-time
  name: string;
  slots: { from: string; to: string }[];
}

// Calculate Easter date for a given year using Gauss algorithm
function calculateEaster(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(year, month - 1, day);
}

// Get next occurrence date for an exception
export function getNextOccurrence(rrule: {
  rrule?: string;
  date?: string;
}): string | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // One-time exception with specific date
  if (rrule.date) {
    const exceptionDate = new Date(rrule.date + "T00:00:00");
    return exceptionDate >= today ? rrule.date : null;
  }

  // RRULE-based recurring exception
  if (rrule.rrule) {
    // Check if it's an Easter-based exception
    const easterMatch = rrule.rrule.match(/BYEASTER=(-?\d+)/);
    if (easterMatch) {
      const easterOffset = parseInt(easterMatch[1]);
      const currentYear = today.getFullYear();
      const nextYear = currentYear + 1;

      for (const year of [currentYear, nextYear]) {
        const easter = calculateEaster(year);
        const targetDate = new Date(easter);
        targetDate.setDate(easter.getDate() + easterOffset);

        if (targetDate >= today) {
          return targetDate.toISOString().split("T")[0];
        }
      }
      return null;
    }

    // Standard RRULE
    try {
      // Parse the RRULE string
      const r = RRule.fromString(rrule.rrule);
      const nextOccurrence = r.after(today, true);

      if (nextOccurrence) {
        return nextOccurrence.toISOString().split("T")[0];
      }
    } catch (e) {
      console.error("Failed to parse RRULE:", rrule.rrule, e);
    }
  }

  return null;
}

export function parseExceptions(rrules: string): Exception[] {
  if (!rrules) return [];

  const exceptions: Exception[] = [];
  const rules = rrules.split("\n").filter((r) => r.trim());

  rules.forEach((rule) => {
    // Parse RRULE-based exceptions
    const rruleMatch = rule.match(
      /EXCEPTION:RRULE=([^;]+);NAME=([^;]+);SLOTS=(.+)/,
    );
    if (rruleMatch) {
      const [, rrule, name, slotsJson] = rruleMatch;
      try {
        const slots = JSON.parse(decodeURIComponent(slotsJson));
        exceptions.push({
          id: `exception-rrule-${Date.now()}-${Math.random()}`,
          rrule: decodeURIComponent(rrule),
          name: decodeURIComponent(name),
          slots,
        });
      } catch (e) {
        console.error("Failed to parse RRULE exception:", e);
      }
      return;
    }

    // Parse date-based exceptions
    const exceptionMatch = rule.match(
      /EXCEPTION:DATE=([^;]+);NAME=([^;]+);SLOTS=(.+)/,
    );
    if (exceptionMatch) {
      const [, date, name, slotsJson] = exceptionMatch;
      try {
        const slots = JSON.parse(decodeURIComponent(slotsJson));
        exceptions.push({
          id: `exception-${date}`,
          date,
          name: decodeURIComponent(name),
          slots,
        });
      } catch (e) {
        console.error("Failed to parse exception:", e);
      }
    }
  });

  return exceptions;
}

export function getNextException(
  rrules: string,
): (Exception & { nextDate: string }) | null {
  const exceptions = parseExceptions(rrules);

  // Get next occurrence for each exception
  const upcomingExceptions = exceptions
    .map((exception) => {
      const nextDate = getNextOccurrence(exception);
      return nextDate ? { ...exception, nextDate } : null;
    })
    .filter(Boolean) as (Exception & { nextDate: string })[];

  // Sort by next occurrence date
  upcomingExceptions.sort((a, b) => a.nextDate.localeCompare(b.nextDate));

  return upcomingExceptions.length > 0 ? upcomingExceptions[0] : null;
}
